import React, { useEffect } from "react";
import { Box } from "@material-ui/core";
import { getCancellationPolicyInfo, useDeviceTypes } from "halifax";
import { Route, useHistory } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";

import {
  CheckoutStateProvider,
  LodgingSelectors,
  RewardsPaymentSelectors,
  useCheckoutStateSelector,
} from "@capone/checkout";
import {
  CancellationPolicyEnum,
  CorpRoomProduct,
  RoomProduct,
  VIEWED_PACKAGE_CHECKOUT,
} from "redmond";
import { PackagesBookConnectorProps } from "./container";
import {
  DesktopPackagesBookWorkflow,
  MobilePackagesBookWorkflow,
  PackageConfirmation,
} from "./components";
import { PATH_BOOK, PATH_BOOK_CONFIRMATION } from "../../utils/paths";
import "./styles.scss";
import { packagesBookStateMachine } from "./state/stateMachine";
import { getInitialPackagesMachineContext } from "./state/initialContext";
import { Actions } from "./state/actions";
import * as services from "./state/services";
import { Guards } from "./state/guards";
import { validateContext } from "./state/utils";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { getPackagesShopPricing } from "./state/selectors/common";

export interface IPackagesBookProps
  extends RouteComponentProps,
    PackagesBookConnectorProps {}

const PackagesBookTracking: React.FC<{
  chosenProduct: RoomProduct | CorpRoomProduct | null;
}> = ({ chosenProduct }) => {
  const selectedLodging = useCheckoutStateSelector(
    LodgingSelectors.getSelectedLodging
  );
  const packagesShopPricing = useCheckoutStateSelector(getPackagesShopPricing);
  const packageShopDiscount = packagesShopPricing?.totalPackageSavings.fiat;

  const earnForSelectedCard = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForSelectedRewardsAccount
  );
  const earnForHighestEarnAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForHighestEarnRewardsAccount
  );
  const earnToUse = earnForSelectedCard || earnForHighestEarnAccount;

  const discount = packageShopDiscount;

  useEffect(() => {
    if (selectedLodging) {
      const offerProperties = {
        viewed_travel_offer: !!selectedLodging?.bestOfferThisLodging,
      };

      const valueProp = (() => {
        switch (true) {
          case !!earnToUse?.value && !!discount?.value:
            return "earn and package savings";
          case !!earnToUse?.value:
            return "earn only";
          case !!discount?.value:
            return "package savings only";
          default:
            return "none";
        }
      })();

      const cancellationPolicyInfo = chosenProduct?.cancellationPolicy
        ? getCancellationPolicyInfo(chosenProduct?.cancellationPolicy)
        : null;

      trackEvent({
        eventName: VIEWED_PACKAGE_CHECKOUT,
        properties: {
          ...offerProperties,
          val_prop: valueProp,
          hotel_cancellation: !!(
            cancellationPolicyInfo &&
            chosenProduct?.cancellationPolicy.CancellationPolicy !==
              CancellationPolicyEnum.NonRefundable
          ),
        },
        encryptedProperties: [
          selectedLodging?.trackingPropertiesV2?.encryptedProperties ?? "",
        ],
      });
    }
  }, [selectedLodging]);

  return null;
};

export const PackagesBook = (props: IPackagesBookProps) => {
  const { location, chosenProduct } = props;

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const history = useHistory();

  return (
    <CheckoutStateProvider
      stateMachine={packagesBookStateMachine}
      actions={Actions}
      guards={Guards}
      context={getInitialPackagesMachineContext()}
      services={services}
      getInitialContext={getInitialPackagesMachineContext}
      validateContext={validateContext as (ctx: unknown) => boolean}
      history={history}
    >
      <Box
        className={clsx(
          "packages-book-root",
          { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
          { mobile: matchesMobile }
        )}
      >
        <PackagesBookTracking chosenProduct={chosenProduct} />
        <Route path={PATH_BOOK} exact>
          <>
            <Box className="packages-book-container">
              {matchesDesktop && <DesktopPackagesBookWorkflow />}
              {matchesMobile && <MobilePackagesBookWorkflow />}
            </Box>
          </>
        </Route>
        <Route path={PATH_BOOK_CONFIRMATION}>
          <PackageConfirmation />
        </Route>
      </Box>
    </CheckoutStateProvider>
  );
};
